import { baseApi } from "@/api/baseApi";
import {
  BusinessReport,
  BusinessReportType,
  FamilySummaryMode,
  PromptAutoRefreshFrequency,
  PromptDataType,
  TemplateCategory,
  TemplateFavorite,
  TemplateVisibility,
} from "@/modules/snapshots/businessReports.types";
import { BUSINESS_REPORTS_TAGS } from "@/modules/snapshots/businessReports.constraints";
import { ParsedTemplatePrompts, ReplacePromptsPayload } from "./types";

interface CreateBusinessReportPayload {
  name: string;
  description: string;
  /** Exactly one of `file` (xlsx/csv upload) or `prompts` (JSON `[{name, prompt}]` written in the app). */
  file?: FileList;
  prompts?: string;
  type?: BusinessReportType;
  category_id?: string | null;
  /** Admins choose; authoring users may only create `private` templates. */
  visibility?: TemplateVisibility;
}

interface UpdateBusinessReportPayload {
  name: string;
  description: string;
  is_active: boolean;
  type?: BusinessReportType;
  category_id?: string | null;
  visibility?: TemplateVisibility;
}

/** Report templates (business reports), their prompts, template categories and favourites. */
export const templatesApi = baseApi.injectEndpoints({
  overrideExisting: false,
  endpoints: builder => ({
    createBusinessReport: builder.mutation<
      void,
      { data: CreateBusinessReportPayload | Partial<UpdateBusinessReportPayload>; id?: string }
    >({
      invalidatesTags: [BUSINESS_REPORTS_TAGS.BUSINESS_REPORTS_TAG],
      query: ({ data, id }) => {
        const formData = new FormData();
        Object.entries(data).forEach(([key, value]) => {
          if (key === "file") {
            if (value?.[0]) formData.append(key, value[0]);
          } else if (value !== undefined && value !== null) {
            formData.append(key, value);
          }
        });

        return {
          headers: {
            "Content-Type": "multipart/form-data;",
          },
          url: id
            ? `/pf-mainweb/business-reports/business-reports/${id}`
            : "/pf-mainweb/business-reports/business-reports",
          method: id ? "PATCH" : "POST",
          data: formData,
        };
      },
    }),
    updateBusinessReport: builder.mutation<
      void,
      { data: CreateBusinessReportPayload | Partial<UpdateBusinessReportPayload>; id?: string }
    >({
      invalidatesTags: [BUSINESS_REPORTS_TAGS.BUSINESS_REPORTS_TAG],
      query: ({ data, id }) => ({
        url: id
          ? `/pf-mainweb/business-reports/business-reports/${id}`
          : "/pf-mainweb/business-reports/business-reports",
        method: id ? "PATCH" : "POST",
        data,
      }),
    }),
    parseBusinessReportTemplate: builder.mutation<ParsedTemplatePrompts, File>({
      query: file => {
        const formData = new FormData();
        formData.append("file", file, file.name);
        return {
          url: "/pf-mainweb/business-reports/business-reports/parse",
          method: "POST",
          headers: { "Content-Type": "multipart/form-data" },
          data: formData,
        };
      },
    }),
    replaceBusinessReportPrompts: builder.mutation<void, ReplacePromptsPayload>({
      invalidatesTags: [BUSINESS_REPORTS_TAGS.BUSINESS_REPORTS_TAG],
      query: ({ id, prompts }) => ({
        url: `/pf-mainweb/business-reports/business-reports/${encodeURIComponent(id)}/prompts`,
        method: "PUT",
        data: { prompts },
      }),
    }),
    deleteBusinessReport: builder.mutation<void, string>({
      invalidatesTags: [BUSINESS_REPORTS_TAGS.BUSINESS_REPORTS_TAG],
      query: id => ({
        url: `/pf-mainweb/business-reports/business-reports/${encodeURIComponent(id)}`,
        method: "DELETE",
      }),
    }),
    updateBusinessReportPrompt: builder.mutation<
      void,
      {
        data: {
          name: string;
          prompt: string;
          full_context: boolean;
          is_date: boolean;
          data_type: PromptDataType | null;
          use_image: boolean;
          no_retriever: boolean;
          web_search: boolean;
          traffic_light?: boolean;
          auto_refresh_frequency?: PromptAutoRefreshFrequency | null;
          family_summary_mode?: FamilySummaryMode;
          family_summary_prompt?: string | null;
          family_metadata?: boolean;
        };
        id?: string;
      }
    >({
      invalidatesTags: [BUSINESS_REPORTS_TAGS.BUSINESS_REPORTS_TAG],
      query: ({ data, id }) => ({
        url: `/pf-mainweb/business-reports/business-report-prompts/${id}`,
        method: "PATCH",
        data,
      }),
    }),
    getBusinessReports: builder.query<BusinessReport[], { type?: BusinessReportType } | void>({
      providesTags: [BUSINESS_REPORTS_TAGS.BUSINESS_REPORTS_TAG],
      query: (params?) => ({
        url: "/pf-mainweb/business-reports/business-report-list",
        params: params?.type ? { type: params.type } : undefined,
      }),
    }),
    getTemplateCategories: builder.query<TemplateCategory[], void>({
      providesTags: [BUSINESS_REPORTS_TAGS.TEMPLATE_CATEGORIES_TAG],
      query: () => ({
        url: "/pf-mainweb/business-reports/template-categories",
      }),
    }),
    createTemplateCategory: builder.mutation<TemplateCategory, { name: string }>({
      invalidatesTags: [BUSINESS_REPORTS_TAGS.TEMPLATE_CATEGORIES_TAG],
      query: data => ({
        url: "/pf-mainweb/business-reports/template-categories",
        method: "POST",
        data,
      }),
    }),
    updateTemplateCategory: builder.mutation<void, { id: string; data: Partial<{ name: string; order: number }> }>({
      invalidatesTags: [BUSINESS_REPORTS_TAGS.TEMPLATE_CATEGORIES_TAG, BUSINESS_REPORTS_TAGS.BUSINESS_REPORTS_TAG],
      query: ({ id, data }) => ({
        url: `/pf-mainweb/business-reports/template-categories/${id}`,
        method: "PATCH",
        data,
      }),
    }),
    deleteTemplateCategory: builder.mutation<void, string>({
      invalidatesTags: [BUSINESS_REPORTS_TAGS.TEMPLATE_CATEGORIES_TAG, BUSINESS_REPORTS_TAGS.BUSINESS_REPORTS_TAG],
      query: id => ({
        url: `/pf-mainweb/business-reports/template-categories/${id}`,
        method: "DELETE",
      }),
    }),
    getTemplateFavorites: builder.query<TemplateFavorite[], void>({
      providesTags: [BUSINESS_REPORTS_TAGS.TEMPLATE_FAVORITES_TAG],
      query: () => ({
        url: "/pf-mainweb/business-reports/business-reports/favorites",
      }),
    }),
    addTemplateFavorite: builder.mutation<void, { business_report_id: string }>({
      invalidatesTags: [BUSINESS_REPORTS_TAGS.TEMPLATE_FAVORITES_TAG],
      query: data => ({
        url: "/pf-mainweb/business-reports/business-reports/favorites",
        method: "POST",
        data,
      }),
    }),
    deleteTemplateFavorite: builder.mutation<{ success: boolean }, string>({
      invalidatesTags: [BUSINESS_REPORTS_TAGS.TEMPLATE_FAVORITES_TAG],
      query: id => ({
        url: `/pf-mainweb/business-reports/business-reports/${id}/favorites`,
        method: "DELETE",
      }),
    }),
  }),
});

export const {
  useGetBusinessReportsQuery,
  useCreateBusinessReportMutation,
  useUpdateBusinessReportMutation,
  useDeleteBusinessReportMutation,
  useUpdateBusinessReportPromptMutation,
  useParseBusinessReportTemplateMutation,
  useReplaceBusinessReportPromptsMutation,
  useGetTemplateCategoriesQuery,
  useCreateTemplateCategoryMutation,
  useUpdateTemplateCategoryMutation,
  useDeleteTemplateCategoryMutation,
  useGetTemplateFavoritesQuery,
  useAddTemplateFavoriteMutation,
  useDeleteTemplateFavoriteMutation,
} = templatesApi;
